import React, { useEffect, useState } from "react";

import Switch from "../../components/Switch";
import BrandTextLink from "../../components/BrandTextLink/BrandTextLink";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchStudentMarketingConsent,
  setStudentMarketingConsent,
} from "../../services/studentPortalService/studentPortalService";
import type { StudentLegalDocument } from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import {
  CoursesPageLead,
  CoursesPageShell,
  CoursesPageTitle,
  DemoLoadingState,
} from "./studentCourses.styles";
import {
  DetailSectionMain,
  DetailSectionSide,
  DetailSectionText,
  DetailSectionTitle,
  TeacherDetailSection,
  TeacherDetailSurface,
} from "./StudentTeachersPage.styles";

const StudentPreferencesPage: React.FC = () => {
  const toast = useNotify();
  const [documents, setDocuments] = useState<StudentLegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    fetchStudentMarketingConsent()
      .then((state) => setDocuments(state.documents || []))
      .catch((error) => {
        logger.error("Ошибка загрузки согласия на рассылку:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить настройки рассылки", life: 3000 });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  const setConsent = async (document: StudentLegalDocument, enabled: boolean) => {
    setBusyId(document.id);
    try {
      await setStudentMarketingConsent(document.id, enabled);
      setDocuments((current) => current.map((item) => item.id === document.id ? { ...item, accepted: enabled } : item));
      toast.show({
        severity: "success",
        summary: "Настройки сохранены",
        detail: enabled ? "Информационная рассылка включена" : "Информационная рассылка отключена",
        life: 3000,
      });
    } catch (error) {
      logger.error("Ошибка изменения согласия на рассылку:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось изменить настройку рассылки", life: 3000 });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <CoursesPageShell>
      <CoursesPageTitle>Настройки</CoursesPageTitle>
      <CoursesPageLead>Управление добровольными информационными рассылками.</CoursesPageLead>
      {loading ? (
        <DemoLoadingState>Загружаем настройки…</DemoLoadingState>
      ) : documents.length === 0 ? (
        <TeacherDetailSurface>
          <TeacherDetailSection>
            <DetailSectionMain>
              <DetailSectionTitle>Информационная рассылка</DetailSectionTitle>
              <DetailSectionText>Согласие на информационные сообщения пока не опубликовано.</DetailSectionText>
            </DetailSectionMain>
          </TeacherDetailSection>
        </TeacherDetailSurface>
      ) : (
        <TeacherDetailSurface>
          {documents.map((document) => (
            <TeacherDetailSection key={document.id}>
              <DetailSectionMain>
                <DetailSectionTitle>Информационная рассылка</DetailSectionTitle>
                <DetailSectionText>
                  {document.url ? <BrandTextLink $size="lg" href={document.url} target="_blank" rel="noreferrer">{document.title}</BrandTextLink> : document.title}
                  <br />
                  Согласие добровольное. Его можно включить или отозвать в любой момент.
                </DetailSectionText>
              </DetailSectionMain>
              <DetailSectionSide>
                <Switch
                  checked={document.accepted}
                  disabled={busyId === document.id}
                  ariaLabel="Получать информационные сообщения"
                  label={document.accepted ? "Рассылка включена" : "Рассылка выключена"}
                  onChange={(enabled) => void setConsent(document, enabled)}
                />
              </DetailSectionSide>
            </TeacherDetailSection>
          ))}
        </TeacherDetailSurface>
      )}
    </CoursesPageShell>
  );
};

export default StudentPreferencesPage;
